import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Building2,
  Folder,
  Home,
  Building,
  Landmark,
  Target,
  ParkingCircle,
  Lock,
  Bike,
  Package,
  Palette,
  Settings,
  Briefcase,
  Users,
  ChevronDown,
  Wrench,
  LayoutDashboard,
  Shield,
  AlertTriangle,
  HardHat,
  ClipboardCheck,
  ListChecks,
  UserCircle,
  ShoppingBag,
  TrendingUp,
  HomeIcon,
  FileText,
  Palette as PaletteIcon,
  SettingsIcon,
  UserCog,
  BarChart3,
  DollarSign,
  LayoutGrid,
  Hammer
} from 'lucide-react';

const SideMenu: React.FC = () => {
  const navigate = useNavigate();
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const location = useLocation();

  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  const menuGroups = [
    {
      id: 'reports',
      label: 'Reports',
      icon: BarChart3,
      items: [
        {
          id: 'reports-dashboard',
          label: 'Dashboard',
          icon: LayoutDashboard,
          path: `/workspaces/${workspaceId}/reports/dashboard`
        },
        {
          id: 'reports-finance',
          label: 'Finance',
          icon: DollarSign,
          path: `/workspaces/${workspaceId}/reports/finance`
        },
        {
          id: 'reports-suite-layouts',
          label: 'Suite Layouts',
          icon: LayoutGrid,
          path: `/workspaces/${workspaceId}/reports/suite-layouts`
        },
        {
          id: 'reports-construction',
          label: 'Construction',
          icon: Hammer,
          path: `/workspaces/${workspaceId}/reports/construction`
        },
        {
          id: 'reports-parking',
          label: 'Parking',
          icon: ParkingCircle,
          path: `/workspaces/${workspaceId}/reports/parking`
        },
        {
          id: 'reports-units',
          label: 'Units',
          icon: Building,
          path: `/workspaces/${workspaceId}/reports/units`
        }
      ]
    },
    {
      id: 'properties',
      label: 'Properties',
      icon: Building2,
      items: [
        {
          id: 'builders',
          label: 'Builders',
          icon: Building2,
          path: `/workspaces/${workspaceId}/builders`
        },
        {
          id: 'projects',
          label: 'Projects',
          icon: Folder,
          path: `/workspaces/${workspaceId}/projects`
        },
        {
          id: 'subdivisions',
          label: 'Subdivisions',
          icon: Home,
          path: `/workspaces/${workspaceId}/subdivisions`
        },
        {
          id: 'models',
          label: 'Models',
          icon: Home,
          path: `/workspaces/${workspaceId}/models`
        },
        {
          id: 'units',
          label: 'Units',
          icon: Building,
          path: `/workspaces/${workspaceId}/units`
        },
        {
          id: 'common-areas',
          label: 'Common Areas',
          icon: Landmark,
          path: `/workspaces/${workspaceId}/common-areas`
        }
      ]
    },
    {
      id: 'amenities',
      label: 'Amenities',
      icon: Target,
      items: [
        {
          id: 'parking',
          label: 'Parking',
          icon: ParkingCircle,
          path: `/workspaces/${workspaceId}/parking`
        },
        {
          id: 'lockers',
          label: 'Lockers',
          icon: Lock,
          path: `/workspaces/${workspaceId}/lockers`
        },
        {
          id: 'bicycles',
          label: 'Bicycles',
          icon: Bike,
          path: `/workspaces/${workspaceId}/bicycles`
        }
      ]
    },
    {
      id: 'packageCenter',
      label: 'Package Center',
      icon: Package,
      items: [
        {
          id: 'color-collections',
          label: 'Color Collections',
          icon: Palette,
          path: `/workspaces/${workspaceId}/color-collections`
        },
        {
          id: 'packages',
          label: 'Packages',
          icon: Package,
          path: `/workspaces/${workspaceId}/packages`
        },
        {
          id: 'options',
          label: 'Options',
          icon: Settings,
          path: `/workspaces/${workspaceId}/options`
        },
        {
          id: 'locations',
          label: 'Locations',
          icon: Target,
          path: `/workspaces/${workspaceId}/locations`
        }
      ]
    },
    {
      id: 'crm',
      label: 'CRM',
      icon: Briefcase,
      items: [
        {
          id: 'contacts',
          label: 'Contacts',
          icon: Users,
          path: `/workspaces/${workspaceId}/contacts`
        },
        {
          id: 'deals',
          label: 'Deals',
          icon: Briefcase,
          path: `/workspaces/${workspaceId}/deals`
        }
      ]
    },
    {
      id: 'service',
      label: 'Service',
      icon: Wrench,
      items: [
        {
          id: 'service-dashboard',
          label: 'Dashboard',
          icon: LayoutDashboard,
          path: `/workspaces/${workspaceId}/service/dashboard`
        },
        {
          id: 'tarion',
          label: 'Tarion',
          icon: Shield,
          path: `/workspaces/${workspaceId}/service/tarion`
        },
        {
          id: 'deficiencies',
          label: 'Deficiencies',
          icon: AlertTriangle,
          path: `/workspaces/${workspaceId}/service/deficiencies`
        },
        {
          id: 'construction-status',
          label: 'Construction Status',
          icon: HardHat,
          path: `/workspaces/${workspaceId}/service/construction-status`
        },
        {
          id: 'inspection',
          label: 'Inspection',
          icon: ClipboardCheck,
          path: `/workspaces/${workspaceId}/service/inspection`
        },
        {
          id: 'checklist',
          label: 'Checklist',
          icon: ListChecks,
          path: `/workspaces/${workspaceId}/service/checklist`
        },
        {
          id: 'service-settings',
          label: 'Settings',
          icon: Settings,
          path: `/workspaces/${workspaceId}/service/settings`
        }
      ]
    },
    {
      id: 'purchaserPortal',
      label: 'Purchaser Portal',
      icon: ShoppingBag,
      items: [
        {
          id: 'project-progress',
          label: 'Project Progress',
          icon: TrendingUp,
          path: `/workspaces/${workspaceId}/purchaser-portal/project-progress`
        },
        {
          id: 'home-owners',
          label: 'Home Owners',
          icon: HomeIcon,
          path: `/workspaces/${workspaceId}/purchaser-portal/home-owners`
        },
        {
          id: 'unit-deal',
          label: 'Unit/Deal',
          icon: FileText,
          path: `/workspaces/${workspaceId}/purchaser-portal/unit-deal`
        },
        {
          id: 'customization',
          label: 'Customization',
          icon: PaletteIcon,
          path: `/workspaces/${workspaceId}/purchaser-portal/customization`
        },
        {
          id: 'portal-admins',
          label: 'Portal Admins',
          icon: UserCog,
          path: `/workspaces/${workspaceId}/purchaser-portal/portal-admins`
        },
        {
          id: 'portal-settings',
          label: 'Portal Settings',
          icon: SettingsIcon,
          path: `/workspaces/${workspaceId}/purchaser-portal/settings`
        }
      ]
    },
    {
      id: 'security',
      label: 'Security',
      icon: Shield,
      items: [
        {
          id: 'users',
          label: 'Users',
          icon: Users,
          path: `/workspaces/${workspaceId}/security/users`
        },
        {
          id: 'roles',
          label: 'Roles',
          icon: UserCircle,
          path: `/workspaces/${workspaceId}/security/roles`
        },
        {
          id: 'settings',
          label: 'Settings',
          icon: Settings,
          path: `/workspaces/${workspaceId}/security/settings`
        }
      ]
    }
  ];

  // Automatically expand the menu group that contains the active page
  useEffect(() => {
    const activeGroup = menuGroups.find(group =>
      group.items.some(item => location.pathname.startsWith(item.path))
    );

    if (activeGroup) {
      // Collapse all groups except the active one
      setExpandedGroups({
        [activeGroup.id]: true
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const isActive = (path: string) => {
    return location.pathname.startsWith(path);
  };

  const toggleGroup = (groupId: string) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  const handleMenuClick = (item: { path: string }) => {
    navigate(item.path);
  };

  return (
    <aside className="app-sidemenu">
      <nav className="app-sidemenu-nav">
        {menuGroups.map((group) => {
          const GroupIcon = group.icon;
          return (
            <div key={group.id} className="app-sidemenu-group">
              <button
                className="app-sidemenu-group-header"
                onClick={() => toggleGroup(group.id)}
              >
                <span className="app-sidemenu-icon">
                  <GroupIcon size={18} />
                </span>
                <span className="app-sidemenu-label">{group.label}</span>
                <span className={`app-sidemenu-chevron ${expandedGroups[group.id] ? 'expanded' : ''}`}>
                  <ChevronDown size={14} />
                </span>
              </button>
              {expandedGroups[group.id] && (
                <div className="app-sidemenu-group-items">
                  {group.items.map((item) => {
                    const ItemIcon = item.icon;
                    return (
                      <button
                        key={item.id}
                        className={`app-sidemenu-item ${isActive(item.path) ? 'active' : ''}`}
                        onClick={() => handleMenuClick(item)}
                      >
                        <span className="app-sidemenu-icon">
                          <ItemIcon size={18} />
                        </span>
                        <span className="app-sidemenu-label">{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </aside>
  );
};

export default SideMenu;
